//libs
import React, { memo, useMemo, ReactElement, useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import isEmpty from 'lodash/isEmpty';
import { generateProductImpressionData } from '@nykaa/utils/retina/helpers';
import get from 'lodash/get';

//styles
import {
  TipTile,
  Product,
  ProductRow,
  Container,
  ProductPlaceholder,
  ImageChild,
  UrgencyNudgeContainer,
  ProductStamp,
  PromotionTextContainer,
} from './styles';

//selectors
import { getProducts, getTotalCount, getPagenumber } from 'shared/store/listingV2/selectors';

//constants
import { CONFIGS, BUTTON_TEXT } from './constants';
import { SCROLLED_ELEMENT } from 'shared/layouts/Listing/Desktop/components/Layout/constants';
import { CONFIG_PAGE_TYPE_PLP, URGENCY_NUDGE_REMOTE_CONFIG } from 'shared/constants/listing';
import { PLP_INTERACTION } from 'shared/helpers/analytics/constants/plpEvents';

//components
import ViewSimilarIconDesktop from 'shared/components/ViewSimilar/Desktop/components/ViewSimilarIconDesktop';
import ViewSimilarWrapperDesktop from 'shared/components/ViewSimilar/components/ViewSimilarWrapperDesktop';
import WishlistWrapper from 'shared/components/WishlistWrapperDesktop';
import PageIO from 'shared/layouts/Listing/Desktop/components/Layout/components/PageIO';
import PriceReveal from 'shared/components/Product/components/PriceReveal';

//helpers
import { onProductClick } from 'shared/layouts/Listing/Desktop/components/AnalyticsV2/helpers';
import { transformProductsToBeautyInterface } from 'shared/helpers/utils/impressions';
import { getDeviceType } from 'shared/components/ResponsiveLoader/helpers';
import { onClickInteraction } from 'shared/layouts/Listing/components/Analytics/helpers';

// hooks
import { useRemoteConfig } from 'shared/store/remoteConfigs/hooks';

/**
 * getComponent
 * @param item as Product or tiptile
 * @returns the rendered component based on the type of element
 */

interface GetComponentProps {
  item: Listing.IProduct | Listing.ITiptile;
  onViewSimilarClick: (
    e: React.SyntheticEvent<HTMLDataElement, Event>,
    products: Listing.IProduct
  ) => void;
  containsResponsible: boolean;
  rowNumber: number;
  urgencyNudgeEnabled: boolean;
  urgencyNudgeTextColor: string;
  trackingMetadata: Record<any, any>;
  isPriceRevealEnabled: boolean;
  salePromotionConfig: {
    desktop: boolean;
    plp: { pipeColor?: string; textColor: string; text: string };
  };
  productStampConfig: { isEnabled: boolean; config: { backgroundColor: string } };
}

const getComponent = ({
  item,
  onViewSimilarClick,
  containsResponsible,
  rowNumber,
  urgencyNudgeEnabled,
  urgencyNudgeTextColor,
  trackingMetadata,
  isPriceRevealEnabled,
  salePromotionConfig,
  productStampConfig,
}: GetComponentProps) => {
  if (item.type === 'tiptile') {
    const tiptile = item as Listing.ITiptile;
    return (
      <TipTile
        item={tiptile}
        imageSrc={tiptile.gridImageUrl}
        width={300}
        onInteraction={() => onProductClick(item as Listing.IProduct)}
        openInTab={true}
        alt={''}
        key={`tiptile-${tiptile.position}`}
      ></TipTile>
    );
  }

  const product = item as Listing.IProduct;

  const showPriceReveal =
    isPriceRevealEnabled &&
    !product?.isOutOfStock &&
    Boolean(product?.price_reveal_data && product?.price_reveal_data.sp);

  const transformedImpressionsProduct = generateProductImpressionData({
    product: transformProductsToBeautyInterface(product),
    additionalProductParams: {
      productPosition: product?.listingPosition || trackingMetadata?.productPosition,
      listingPosition: trackingMetadata?.widgetPosition,
      listingIdentifier: trackingMetadata?.widgetIdentifier,
      isListingDataRequired: !isEmpty(trackingMetadata),
    },
  });
  const showUrgencyNudge =
    urgencyNudgeEnabled && product?.urgencyTag?.length && product.isOutOfStock !== 1;

  const showPromotionText =
    isPriceRevealEnabled &&
    product.sale_price_live_flag &&
    salePromotionConfig.desktop &&
    !showUrgencyNudge;

  const getProductStamp = () => {
    const { isEnabled, config } = productStampConfig;

    if (!isEnabled || !product.product_stamps_value) {
      return null;
    }

    return (
      <ProductStamp backgroundColor={config?.backgroundColor}>
        {product.product_stamps_value}
      </ProductStamp>
    );
  };

  return (
    <Product
      key={product.id}
      item={product}
      onInteraction={() => onProductClick(item as Listing.IProduct)}
      containsResponsible={containsResponsible}
      DescriptionChildren={
        <>
          {showPriceReveal ? <PriceReveal priceRevealData={product?.price_reveal_data} /> : null}
          {showUrgencyNudge ? (
            <UrgencyNudgeContainer
              promotionTextConfig={{
                textColor: urgencyNudgeTextColor,
                text: product?.urgencyTag,
              }}
            />
          ) : null}
          {showPromotionText ? (
            <PromotionTextContainer promotionTextConfig={salePromotionConfig.plp} />
          ) : null}
        </>
      }
      ImageChildren={
        <>
          <WishlistWrapper
            item={product}
            transformedImpressionsProduct={transformedImpressionsProduct}
            onClickInteraction={() => onClickInteraction(PLP_INTERACTION.WISHLIST)}
          />

          <ImageChild>
            {product?.has_visual_similarity_recos && (
              <ViewSimilarIconDesktop onClick={(e) => onViewSimilarClick(e, product)} />
            )}
          </ImageChild>
        </>
      }
      isImageLazy={rowNumber > 1}
      fallback={<ProductPlaceholder />}
      ProductStamp={getProductStamp()}
    />
  );
};

const Products = () => {
  const products = useSelector(getProducts);
  const [selectedProduct, setSelectedProduct] = useState<Listing.IProduct | null>();
  const [showViewSimilarSheet, setShowViewSimilarSheet] = useState(false);
  const totalCount = useSelector(getTotalCount);
  const pageNumber = useSelector(getPagenumber);
  const isLastPage = Math.ceil(totalCount / CONFIGS.PAGE_SIZE) <= Number(pageNumber);
  const [activeElement, setActiveElement] = useState<number | null>(SCROLLED_ELEMENT.PAGEIO);
  const {
    mweb: urgencyNudgeMweb,
    dweb: urgencyNudgeDweb,
    textColor: urgencyNudgeTextColor,
  } = get(useRemoteConfig(URGENCY_NUDGE_REMOTE_CONFIG), CONFIG_PAGE_TYPE_PLP, {
    mweb: false,
    dweb: false,
    textColor: '',
  });
  const {
    desktop: isPriceRevealEnabled = false,
    salePromotionConfig = { desktop: false, plp: { pipeColor: '', textColor: '', text: '' } },
  } = useRemoteConfig('priceReveal');
  const productStampConfig = useRemoteConfig('productStamp');
  const urgencyNudgeEnabled = getDeviceType() === 0 ? urgencyNudgeMweb : urgencyNudgeDweb;

  const onViewSimilarClick = (
    e: React.SyntheticEvent<HTMLDataElement, Event>,
    product: Listing.IProduct
  ) => {
    e.stopPropagation();
    e.preventDefault();
    setShowViewSimilarSheet(true);
    setSelectedProduct(product);
    onClickInteraction(PLP_INTERACTION.VIEW_SIMILAR);
  };

  useEffect(() => {
    setActiveElement(SCROLLED_ELEMENT.PAGEIO);
  }, [products]);

  const renderPlaceholders = () => {
    if (isLastPage) return null;
    return (
      <>
        <ProductRow key={`placeholders-row`} initialVisible>
          {Array(CONFIGS.NUMBER_OF_ITEMS_IN_ROW)
            .fill('_')
            .map((_, placeholderIndex) => (
              <ProductPlaceholder key={`placeholder-${placeholderIndex}`} />
            ))}
        </ProductRow>
        <PageIO
          setActiveElement={setActiveElement}
          isLastPage={isLastPage}
          buttonText={BUTTON_TEXT.LOADING}
          isLoading
        />
      </>
    );
  };

  const ProductRows = useMemo(() => {
    let renderableRows: ReactElement[] = [];
    const maxRows = Math.ceil(products.length / CONFIGS.NUMBER_OF_ITEMS_IN_ROW);

    //iterating to max possible rows
    Array(maxRows)
      .fill('_')
      .forEach((_, index) => {
        //getting components for each row by slicing the array from n to n+4th index
        // eg= if row=1 (index:0) get products 0 to 3
        //     if row=2 (index:1) get products 4 to 7
        //this logic is followed by a map to get the output as react child
        let containsResponsible = false;
        const rowProducts = products.slice(
          index * CONFIGS.NUMBER_OF_ITEMS_IN_ROW,
          (index + 1) * CONFIGS.NUMBER_OF_ITEMS_IN_ROW
        );
        rowProducts.map((item) => {
          if (!isEmpty(item.token) && !containsResponsible) {
            containsResponsible = true;
          }
        });
        const rowNodes = rowProducts.map((item) => {
          return getComponent({
            item,
            onViewSimilarClick,
            containsResponsible,
            rowNumber: index,
            urgencyNudgeEnabled,
            urgencyNudgeTextColor,
            trackingMetadata: {},
            isPriceRevealEnabled,
            salePromotionConfig,
            productStampConfig,
          });
        });

        //filler logic of placeholder in case row has less than 4 products
        if (rowNodes.length < CONFIGS.NUMBER_OF_ITEMS_IN_ROW && !isLastPage) {
          const placeholders = Array(CONFIGS.NUMBER_OF_ITEMS_IN_ROW - rowNodes.length)
            .fill('_')
            .map((__, placeholderIndex) => (
              <ProductPlaceholder key={`placeholder-${index}-${placeholderIndex}`} />
            ));
          rowNodes.push(...placeholders);
        }

        renderableRows.push(
          <ProductRow key={`row-${index}`} initialVisible>
            {rowNodes}
          </ProductRow>
        );
      });
    return renderableRows;
  }, [
    isLastPage,
    isPriceRevealEnabled,
    products,
    salePromotionConfig,
    urgencyNudgeEnabled,
    urgencyNudgeTextColor,
    productStampConfig,
  ]);

  return (
    <>
      <Container>
        {ProductRows}
        {activeElement === SCROLLED_ELEMENT.PLACEHOLDER && renderPlaceholders()}
        {activeElement === SCROLLED_ELEMENT.PAGEIO && (
          <PageIO
            setActiveElement={setActiveElement}
            isLastPage={isLastPage}
            buttonText={BUTTON_TEXT.LOAD_MORE}
          />
        )}
      </Container>
      {showViewSimilarSheet && selectedProduct && (
        <ViewSimilarWrapperDesktop
          productID={selectedProduct?.id}
          closeSheet={() => {
            setSelectedProduct(null);
            setShowViewSimilarSheet(false);
          }}
          selectedProduct={selectedProduct}
        />
      )}
    </>
  );
};

export default memo(Products);
